import React from 'react';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  BarChart3,
  ReceiptText,
  TrendingUp,
  WalletCards,
} from 'lucide-react';

import type { PassbookSummary as PassbookSummaryData } from '../types';

interface PassbookSummaryProps {
  summary: PassbookSummaryData | null;
  loading?: boolean;
}

const formatCurrency = (value: number | null | undefined) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const PassbookSummary: React.FC<PassbookSummaryProps> = ({
  summary,
  loading = false,
}) => {
  const cards = [
    {
      label: 'Total Transactions',
      value: summary?.total_transactions ?? 0,
      format: 'number',
      icon: ReceiptText,
    },
    {
      label: 'Total Deposits',
      value: summary?.total_deposits ?? 0,
      format: 'currency',
      icon: ArrowDownCircle,
    },
    {
      label: 'Total Withdrawals',
      value: summary?.total_withdrawals ?? 0,
      format: 'currency',
      icon: ArrowUpCircle,
    },
    {
      label: 'Total Wagered',
      value: summary?.total_wagered ?? 0,
      format: 'currency',
      icon: WalletCards,
    },
    {
      label: 'Total Won',
      value: summary?.total_won ?? 0,
      format: 'currency',
      icon: TrendingUp,
    },
    {
      label: 'Net Profit',
      value: summary?.net_profit ?? 0,
      format: 'currency',
      icon: BarChart3,
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {cards.map((card) => {
        const Icon = card.icon;

        return (
          <div
            key={card.label}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {card.label}
                </p>

                <p className="mt-3 text-2xl font-semibold text-slate-900">
                  {loading
                    ? 'Loading...'
                    : card.format === 'currency'
                      ? formatCurrency(Number(card.value))
                      : Number(card.value).toLocaleString('en-IN')}
                </p>
              </div>

              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                <Icon className="h-5 w-5" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PassbookSummary;
